import React, { useState } from "react"

import { format } from "date-fns"
import { ChevronDown, ChevronRight, ChevronsUpDown, Lock, Unlock } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DAY_LABELS, DAY_NAMES, DAY_SHORT_LABELS } from "@/lib/collectionCoverage"
import { formatDayNumeric } from "@/lib/dateFormats"
import { formatCost } from "@/lib/routeCostCalculation"
import { cn } from "@/lib/utils"
import { RouteWeeklyData, WeekData, formatWeekCell } from "@/lib/weeklyPlan"

import RevenueByRouteSheet from "./RevenueByRouteSheet"
import { NetTotalRow, SummaryRow } from "./SummaryRow"
import { COMPACT_DAY_PX, COMPACT_TABLE_MIN_WIDTH } from "./compactColumns"

interface WeekTableProps {
  weekDays: Date[]
  data: WeekData
  compact?: boolean
  compactSummary?: boolean
  defaultCollapsed?: boolean
  onRouteClick?: (route: RouteWeeklyData, dateStr: string, dayLabel: string) => void
}

const WeekTable: React.FC<WeekTableProps> = ({
  weekDays,
  data,
  compact,
  compactSummary,
  defaultCollapsed,
  onRouteClick,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false)
  const [revenueDetail, setRevenueDetail] = useState<{
    dateStr: string | null
    dayLabel: string
  } | null>(null)
  const { categories, dailySummaries, weekTotals } = data

  const toggleCategory = (name: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const catColW = compact
    ? "w-[100px] min-w-[100px] max-w-[100px]"
    : "w-[140px] min-w-[140px] max-w-[140px]"
  const routeColW = compact
    ? "w-[150px] min-w-[150px] max-w-[150px]"
    : "w-[200px] min-w-[200px] max-w-[200px]"
  const leftOffset = compact ? "left-[100px]" : "left-[140px]"

  const compactColgroup = compact ? (
    <colgroup>
      <col style={{ width: 100, minWidth: 100 }} />
      <col style={{ width: 150, minWidth: 150 }} />
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <col key={i} style={{ width: COMPACT_DAY_PX, minWidth: COMPACT_DAY_PX }} />
      ))}
      <col style={{ width: COMPACT_DAY_PX, minWidth: COMPACT_DAY_PX }} />
    </colgroup>
  ) : null

  return (
    <div className="rounded-lg border border-palette-jungleGreen200 overflow-hidden bg-white">
      <table
        className="w-full border-collapse text-sm"
        style={compact ? { tableLayout: "fixed", minWidth: COMPACT_TABLE_MIN_WIDTH } : undefined}
      >
        {compactColgroup}
        <thead className="sticky top-0 z-20">
          <tr className="bg-palette-jungleGreen800 text-white">
            <th
              colSpan={2}
              className={cn(
                "sticky left-0 z-30 bg-palette-jungleGreen800 px-3 py-2 text-left text-xs font-normal",
                catColW,
              )}
            >
              {data.isLocked && (
                <Badge
                  variant="outline"
                  className="border-amber-400 text-amber-300 text-[10px] py-0 px-1.5"
                >
                  <Lock className="h-2.5 w-2.5 mr-0.5" />
                  Locked
                </Badge>
              )}
              {!data.isLocked && (
                <Badge
                  variant="outline"
                  className="border-blue-400 text-blue-300 text-[10px] py-0 px-1.5"
                >
                  <Unlock className="h-2.5 w-2.5 mr-0.5" />
                  Live
                </Badge>
              )}
            </th>
            <th colSpan={7} className="px-3 py-1.5" />
            <th className="px-3 py-2 text-right">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-6 px-2 text-[10px] bg-palette-jungleGreen700 hover:bg-palette-jungleGreen600 text-palette-jungleGreen200 border-0 gap-1"
                onClick={() => setCollapsed((p) => !p)}
                title={collapsed ? "Show routes" : "Show summary only"}
              >
                <ChevronsUpDown className="h-3 w-3" />
                {collapsed ? "Show routes" : "Summary only"}
              </Button>
            </th>
          </tr>
          <tr className="bg-palette-jungleGreen100 border-b border-palette-jungleGreen300">
            <th
              className={cn(
                "sticky left-0 z-30 bg-palette-jungleGreen100 px-3 py-2 text-left text-xs font-semibold text-palette-jungleGreen700 border-r border-palette-jungleGreen200",
                catColW,
              )}
            >
              Category
            </th>
            <th
              className={cn(
                "sticky z-30 bg-palette-jungleGreen100 px-3 py-2 text-left text-xs font-semibold text-palette-jungleGreen700 border-r border-palette-jungleGreen200",
                leftOffset,
                routeColW,
              )}
            >
              Route
            </th>
            {weekDays.map((day, i) => (
              <th
                key={i}
                className={cn(
                  "px-3 py-2 text-center text-xs font-semibold text-palette-jungleGreen700 border-r border-palette-jungleGreen200",
                  compact ? "min-w-[80px]" : "min-w-[100px]",
                )}
              >
                <div>{compact ? DAY_SHORT_LABELS[DAY_NAMES[i]] : DAY_LABELS[DAY_NAMES[i]]}</div>
                <div className="font-normal text-palette-jungleGreen400">
                  {formatDayNumeric(day)}
                </div>
              </th>
            ))}
            <th
              className={cn(
                "px-3 py-2 text-center text-xs font-bold text-palette-jungleGreen900 bg-palette-jungleGreen200",
                compact ? "min-w-[80px]" : "min-w-[100px]",
              )}
            >
              Week
            </th>
          </tr>
        </thead>

        {!collapsed && (
          <tbody>
            {categories.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-16 text-palette-jungleGreen400 text-sm">
                  No routes planned for this week
                </td>
              </tr>
            )}
            {categories.map((cat) => {
              const expanded = expandedCategories.has(cat.name)
              const catDayCost = (dateStr: string) =>
                cat.routes.reduce(
                  (s, r) =>
                    s +
                    (r.dayCosts[dateStr]?.netCost ?? r.dayCosts[dateStr]?.labourAndCharging ?? 0),
                  0,
                )
              const catWeekCost = cat.routes.reduce((s, r) => s + r.weekCost, 0)
              return (
                <React.Fragment key={cat.name}>
                  <tr
                    className="border-b border-palette-jungleGreen200 bg-palette-jungleGreen000 hover:bg-palette-jungleGreen100 cursor-pointer select-none"
                    onClick={() => toggleCategory(cat.name)}
                  >
                    <td
                      className={cn(
                        "sticky left-0 z-10 bg-palette-jungleGreen000 px-3 py-2 text-xs font-semibold text-palette-jungleGreen800 border-r border-palette-jungleGreen200",
                        catColW,
                      )}
                    >
                      <div className="flex items-center gap-1">
                        {expanded ? (
                          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-palette-jungleGreen500" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-palette-jungleGreen500" />
                        )}
                        <span className="truncate">{cat.name}</span>
                        <span className="text-[10px] font-normal text-palette-jungleGreen400 ml-0.5">
                          ({cat.routes.length})
                        </span>
                      </div>
                    </td>
                    <td
                      className={cn(
                        "sticky z-10 bg-palette-jungleGreen000 border-r border-palette-jungleGreen200",
                        leftOffset,
                        routeColW,
                      )}
                    />
                    {weekDays.map((day, i) => (
                      <td
                        key={i}
                        className="text-center px-3 py-2 text-xs font-semibold text-palette-jungleGreen700 border-r border-palette-jungleGreen200 tabular-nums"
                      >
                        {formatWeekCell(catDayCost(format(day, "yyyy-MM-dd")))}
                      </td>
                    ))}
                    <td className="text-center px-3 py-2 text-xs font-bold text-palette-jungleGreen900 bg-palette-jungleGreen100 tabular-nums">
                      {formatWeekCell(catWeekCost)}
                    </td>
                  </tr>
                  {expanded &&
                    cat.routes.map((rd) => (
                      <tr
                        key={rd.routeId}
                        className="border-b border-palette-jungleGreen100 hover:bg-blue-50/40"
                      >
                        <td
                          className={cn(
                            "sticky left-0 z-10 bg-white px-3 py-1.5 border-r border-palette-jungleGreen200",
                            catColW,
                          )}
                        />
                        <td
                          className={cn(
                            "sticky z-10 bg-white px-3 py-1.5 text-xs font-medium text-palette-jungleGreen600 border-r border-palette-jungleGreen200 truncate",
                            leftOffset,
                            routeColW,
                          )}
                          title={rd.code}
                        >
                          {rd.code}
                        </td>
                        {weekDays.map((day, i) => {
                          const dateStr = format(day, "yyyy-MM-dd")
                          const dc = rd.dayCosts[dateStr]
                          const clickable = dc?.active && onRouteClick
                          return (
                            <td
                              key={i}
                              className={cn(
                                "text-center px-3 py-1.5 text-xs border-r border-palette-jungleGreen200 tabular-nums",
                                dc?.active
                                  ? "text-palette-jungleGreen600"
                                  : "text-palette-jungleGreen300",
                                clickable &&
                                  "cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors",
                              )}
                              onClick={
                                clickable
                                  ? (e) => {
                                      e.stopPropagation()
                                      onRouteClick!(
                                        rd,
                                        dateStr,
                                        `${compact ? DAY_SHORT_LABELS[DAY_NAMES[i]] : DAY_LABELS[DAY_NAMES[i]]} ${formatDayNumeric(day)}`,
                                      )
                                    }
                                  : undefined
                              }
                            >
                              {formatWeekCell(dc?.netCost ?? dc?.labourAndCharging ?? 0)}
                            </td>
                          )
                        })}
                        <td className="text-center px-3 py-1.5 text-xs font-medium text-palette-jungleGreen800 bg-palette-jungleGreen000 tabular-nums">
                          {formatWeekCell(rd.weekCost)}
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              )
            })}
          </tbody>
        )}

        {categories.length > 0 && (
          <tfoot className="bg-palette-jungleGreen000 border-t-2 border-palette-jungleGreen300">
            <SummaryRow
              label="Total Routes"
              bold
              borderTop
              callout={!compactSummary}
              weekDays={weekDays}
              getValue={(ds) => String(ds.routeCount)}
              weekValue={String(weekTotals.routeCount)}
              dailySummaries={dailySummaries}
              align="center"
            />
            <SummaryRow
              label="Labour Cost"
              callout={!compactSummary}
              weekDays={weekDays}
              getValue={(ds) => formatCost(ds.labourCost)}
              weekValue={formatCost(weekTotals.labourCost)}
              dailySummaries={dailySummaries}
            />
            <SummaryRow
              label="Charging Cost"
              callout={!compactSummary}
              weekDays={weekDays}
              getValue={(ds) => formatCost(ds.chargingCost)}
              weekValue={formatCost(weekTotals.chargingCost)}
              dailySummaries={dailySummaries}
            />
            <SummaryRow
              label="Haulage Revenue £"
              callout={!compactSummary}
              weekDays={weekDays}
              getValue={(ds) =>
                ds.haulageRevenue > 0 ? `-${formatCost(ds.haulageRevenue)}` : "£0.00"
              }
              weekValue={
                weekTotals.haulageRevenue > 0
                  ? `-${formatCost(weekTotals.haulageRevenue)}`
                  : "£0.00"
              }
              dailySummaries={dailySummaries}
              valueClassName={(ds) =>
                ds.haulageRevenue > 0 ? "text-green-600 font-medium" : undefined
              }
              weekClassName={weekTotals.haulageRevenue > 0 ? "text-green-600" : undefined}
              onCellClick={(payload) => setRevenueDetail(payload)}
            />
            <SummaryRow
              label="Total Vehicle Cost"
              callout={!compactSummary}
              weekDays={weekDays}
              getValue={(ds) => formatCost(ds.vehicleCost)}
              weekValue={formatCost(weekTotals.vehicleCost)}
              dailySummaries={dailySummaries}
            />
            <NetTotalRow
              compact={compactSummary}
              weekDays={weekDays}
              dailySummaries={dailySummaries}
              weekTotals={weekTotals}
            />
          </tfoot>
        )}
      </table>
      <RevenueByRouteSheet
        open={revenueDetail !== null}
        onClose={() => setRevenueDetail(null)}
        data={data}
        dateStr={revenueDetail?.dateStr ?? null}
        dayLabel={revenueDetail?.dayLabel ?? ""}
        weekDays={weekDays}
      />
    </div>
  )
}

export default WeekTable
